import { ReactNode, useEffect, useRef } from "react";
import { Timer } from "@claroty/utilities/data/timeUtils";
import { useMemoOnce } from "@claroty/utilities/react/tsHooks";
import { PERFORMANCE_ACTIONS, QUEUES } from "@claroty/utilities/tracking/trackingConsts";
import { _trackEventWithDOMContext } from "@claroty/utilities/tracking/trackingCore";
import { PageLoadSessionManager } from "./PageLoadSessionManager";
import { sendPageLoadToFullStory } from "./sendPageLoadToFullStory";
import { PageLoadMetrics } from "./types";

interface PageLoadTrackerProviderProps {
    children: ReactNode;
    /** Route path for tracking */
    routePath: string;
    /** Optional tracking name for the route */
    routeTrackingName?: string;
    /** Whether to track this page load (default: true) */
    shouldTrackLoading?: boolean;
}

/**
 * PageLoadTrackerProvider - Tracks page load sessions for a route (React only)
 *
 * This component is an optional React wrapper around PageLoadSessionManager.
 * For vanilla JS apps, use PageLoadSessionManager directly.
 *
 * This component:
 * - Starts a new page load session when mounted
 * - Sends performance event when loading completes (all requests done)
 * - Ends the session when unmounted (route change)
 *
 * Usage:
 * Wrap route components with this provider (separate from RouteLoadingProvider)
 *
 * Note: This is optional - you can use PageLoadSessionManager directly in vanilla JS
 */
export function PageLoadTrackerProvider({
    children,
    routePath,
    routeTrackingName,
    shouldTrackLoading = true,
}: PageLoadTrackerProviderProps) {
    // Create timer on mount to track total duration
    const timer = useMemoOnce(() => new Timer());
    const hasTrackedLoadingComplete = useRef(false);
    const selfRef = useRef<HTMLDivElement>(null);

    // Start session on mount
    const pageLoadId = useMemoOnce(() => {
        if (!shouldTrackLoading) return null;
        return PageLoadSessionManager.startSession(routePath, routeTrackingName);
    });

    // Subscribe to loading_complete event and send tracking
    useEffect(() => {
        if (!pageLoadId || !shouldTrackLoading) return;

        const unsubscribe = PageLoadSessionManager.subscribe(event => {
            // Get current pageLoadId dynamically in case it changed
            const currentPageLoadId = PageLoadSessionManager.getCurrentPageLoadId();

            // Check if this event is for the current session
            const isCurrentSession = event.pageLoadId === currentPageLoadId;

            // Check if this event matches our original session
            const matchesOriginalSession = event.pageLoadId === pageLoadId;

            // Check if routePath matches (for loading_complete events)
            let matchesRoute = false;
            if (event.type === "loading_complete" && event.data) {
                const metrics = event.data as PageLoadMetrics;
                matchesRoute = metrics.routePath === routePath;
            }

            // Accept if:
            // 1. Matches original session ID, OR
            // 2. Matches current session ID AND routePath matches (new session for same route)
            const shouldAccept = matchesOriginalSession || (isCurrentSession && matchesRoute);

            if (event.type === "loading_complete" && shouldAccept && !hasTrackedLoadingComplete.current) {
                hasTrackedLoadingComplete.current = true;
                const metrics = event.data as PageLoadMetrics;
                const duration = timer.getDuration();

                const trackingEvent = {
                    action: PERFORMANCE_ACTIONS.PAGE_LOAD_DURATION,
                    name: routeTrackingName || routePath,
                    routePath,
                    duration,
                    requestCount: metrics.requestCount,
                    successfulRequests: metrics.successfulRequests,
                    failedRequests: metrics.failedRequests,
                    slowestRequestUrl: metrics.slowestRequestUrl,
                    slowestRequestDuration: metrics.slowestRequestDuration,
                    maxConcurrentRequests: metrics.maxConcurrentRequests,
                    averageRequestDuration: metrics.averageRequestDuration,
                    totalLoadDuration: metrics.totalDuration,
                };

                // Send performance tracking event to Splunk
                _trackEventWithDOMContext(
                    trackingEvent,
                    selfRef.current?.parentElement ?? document.body,
                    QUEUES.PERFORMANCE,
                );

                // Send performance tracking event to FullStory
                sendPageLoadToFullStory(metrics, routeTrackingName, routePath, duration);
            }
        });

        return () => {
            unsubscribe();
            // End session on unmount
            PageLoadSessionManager.endSession(pageLoadId);
        };
    }, [pageLoadId, routePath, routeTrackingName, shouldTrackLoading, timer]);

    if (!shouldTrackLoading) {
        return <>{children}</>;
    }

    return (
        <>
            <div ref={selfRef} style={{ display: "none" }} />
            {children}
        </>
    );
}

export default PageLoadTrackerProvider;
